import { Box, Menu, MenuItem } from "@mui/material";
import { IconChevronLeft } from "@tabler/icons-react";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Button from "../../../components/ui/Button";
import Text from "../../../components/ui/Text";
import { useToast } from "../../../contexts/ToastContext";
import apiService from "../../../services/api";
import { Team } from "./parts";

const ManageTeams = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { gameId } = useParams();
  const { showToast } = useToast();

  const { team_summary, data } = location.state || {};

  const players = (data || []).sort((a, b) => {
    if (a.join_date && b.join_date) {
      return new Date(a.join_date) - new Date(b.join_date);
    }
    return 0;
  });

  const [saving, setSaving] = useState(false);
  const [teams, setTeams] = useState(
    Object.entries(team_summary || {}).map(([key, value]) => ({
      team_id: key,
      name: value.team_name,
      players: players.filter((p) => p.team_id === key),
    })),
  );

  // State for player menu
  const [playerMenuAnchor, setPlayerMenuAnchor] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);

  const handleBack = () => {
    navigate(`/game-details/${gameId}`);
  };

  const handlePlayerMenuOpen = (event, player, teamId) => {
    setPlayerMenuAnchor(event.currentTarget);
    setSelectedPlayer(player);
    setSelectedTeam(teamId);
  };

  const handlePlayerMenuClose = () => {
    setPlayerMenuAnchor(null);

    setTimeout(() => {
      setSelectedPlayer(null);
      setSelectedTeam(null);
    }, 200);
  };

  const handleMoveToOtherTeam = (targetTeamId) => {
    if (!selectedPlayer || !selectedTeam || selectedTeam === targetTeamId) {
      return;
    }

    setTeams((prevTeams) =>
      prevTeams.map((team) => {
        // Remove player from source team
        if (team.team_id === selectedTeam) {
          return {
            ...team,
            players: team.players.filter(
              (p) => p.player_id !== selectedPlayer.player_id,
            ),
          };
        }
        // Add player to target team
        if (team.team_id === targetTeamId) {
          return {
            ...team,
            players: [...team.players, selectedPlayer].sort((a, b) => {
              if (a.join_date && b.join_date) {
                return new Date(a.join_date) - new Date(b.join_date);
              }
              return 0;
            }),
          };
        }
        return team;
      }),
    );

    handlePlayerMenuClose();
  };

  const handleRandomizeTeams = () => {
    // Collect all players from all teams
    const allPlayers = teams.flatMap((team) => team.players);

    // Shuffle the players array using Fisher-Yates algorithm
    const shuffledPlayers = [...allPlayers];
    for (let i = shuffledPlayers.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledPlayers[i], shuffledPlayers[j]] = [
        shuffledPlayers[j],
        shuffledPlayers[i],
      ];
    }

    // Calculate how many players per team
    const totalPlayers = shuffledPlayers.length;
    const numberOfTeams = teams.length;
    const playersPerTeam = Math.floor(totalPlayers / numberOfTeams);
    const remainingPlayers = totalPlayers % numberOfTeams;

    // Distribute players evenly across teams
    let playerIndex = 0;
    setTeams((prevTeams) =>
      prevTeams.map((team, index) => {
        // Some teams get an extra player if there are remaining players
        const teamSize = playersPerTeam + (index < remainingPlayers ? 1 : 0);
        const teamPlayers = shuffledPlayers.slice(
          playerIndex,
          playerIndex + teamSize,
        );
        playerIndex += teamSize;

        return {
          ...team,
          players: teamPlayers.sort((a, b) => {
            if (a.join_date && b.join_date) {
              return new Date(a.join_date) - new Date(b.join_date);
            }
            return 0;
          }),
        };
      }),
    );
  };

  const handleSave = async () => {
    if (saving) return; // Prevent multiple saves
    setSaving(true);
    // Prepare payload
    const payload = {
      team_assignments: teams.flatMap((team) =>
        team.players.map((player) => ({
          player_id: player.player_id,
          team_id: team.team_id,
        })),
      ),
      team_names: teams.map((team) => ({
        team_id: team.team_id,
        name: team.name,
      })),
    };

    try {
      const response = await apiService.saveTeamAssignments(gameId, payload);

      if (response.success) {
        showToast(response.message, "success");
        navigate(`/game-details/${gameId}`);
      }
    } catch (error) {
      showToast(error.error, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleTeamNameChange = (teamId, name) => {
    setTeams((prevTeams) =>
      prevTeams.map((team) =>
        team.team_id === teamId ? { ...team, name } : team,
      ),
    );
  };

  return (
    <Box
      maxWidth="lg"
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 0, md: 4 },
        alignItems: { xs: "stretch", md: "flex-start" },
      }}
    >
      <Box
        sx={{
          flex: { xs: "none", md: 1 },
          backgroundColor: { xs: "transparent", md: "white" },
          borderRadius: { xs: 0, md: "8px" },
          boxShadow: { xs: "none", md: "0 2px 8px rgba(0,0,0,0.1)" },
          overflow: "hidden",
          p: { xs: 0, md: 3 },
        }}
      >
        {/* Responsive Back Button and Header */}
        <Box sx={{ mb: 3, display: { xs: "block", md: "none" } }}>
          <Button variant="back" onClick={handleBack} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <Box
            onClick={handleBack}
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              cursor: "pointer",
              color: "#1890FF",
              "&:hover": {
                color: "#40A9FF",
              },
            }}
          >
            <IconChevronLeft size={20} />
          </Box>
          <Text variant="pageHeader">Manage teams</Text>
        </Box>

        {teams.map((team) => (
          <Team
            key={team.team_id}
            teamId={team.team_id}
            players={team.players}
            name={team.name}
            setTeamName={(name) => {
              handleTeamNameChange(team.team_id, name);
            }}
            handlePlayerMenuOpen={(event, player) =>
              handlePlayerMenuOpen(event, player, team.team_id)
            }
          />
        ))}

        {/* Player Menu */}
        <Menu
          anchorEl={playerMenuAnchor}
          open={Boolean(playerMenuAnchor)}
          onClose={handlePlayerMenuClose}
          slotProps={{
            paper: {
              sx: {
                borderRadius: 2,
                minWidth: "180px",
              },
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          {teams
            .filter((team) => team.team_id !== selectedTeam)
            .map((team) => (
              <MenuItem
                key={team.team_id}
                onClick={() => handleMoveToOtherTeam(team.team_id)}
                sx={{
                  fontSize: "14px",
                  py: 0.5,
                  px: 2,
                }}
              >
                Move to {team.name || `Team ${team.team_id}`}
              </MenuItem>
            ))}
        </Menu>
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          position: { xs: "fixed", md: "static" },
          display: "flex",
          flexDirection: { xs: "column", sm: "row", md: "column" },
          gap: 2,
          p: 2,
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          minWidth: { xs: "auto", md: "200px", lg: "272px" },
          bottom: { xs: 0, md: "auto" },
          left: { xs: 0, md: "auto" },
          right: { xs: 0, md: "auto" },
          zIndex: 10,
        }}
      >
        <Button
          disabled={saving}
          variant="secondary"
          onClick={handleRandomizeTeams}
        >
          Randomise teams
        </Button>
        <Button disabled={saving} variant="primary" onClick={handleSave}>
          {saving ? "Saving..." : "Save Teams"}
        </Button>
      </Box>
    </Box>
  );
};

export default ManageTeams;
